import { create } from "zustand";
import { NavigateFunction } from "react-router-dom";
import React from "react";
import { createApi } from "../../../utils/createApi";
import { AxiosError } from "axios";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import {
  MetadataTagZones,
  MetadataTagZone,
  MetricLabel,
} from "../EditMetadataTagZonesUtils/editIMetadataTagZonesInterfaces";
import {
  getNumbersAfterEditCamera,
  getNumbersAfterSites,
} from "../../EditCamera/EditCamera";
import { resizeImageMetadataTagZones } from "../EditMetadataTagZonesUtils/resizeImageMetadataTagZones";
import { CameraInfo } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";

const adjustDimensions = (dimensions: string) => {
  const [width, height] = dimensions.split(" x ").map(Number);
  if (!width || !height) return; // Exit if parsing fails

  const aspectRatio = height / width;
  const adjustedHeight = Math.round(720 * aspectRatio);

  return { x: 720, y: adjustedHeight };
};

interface EditIMetadataTagZonesStore {
  metadataTagZones: MetadataTagZones;
  setMetadataTagZones: (metadataTagZones: MetadataTagZones) => void;

  cameraInfo: any;
  setCameraInfo: (cameraInfo: any) => void;

  breadcrumbs: any[];
  setBreadcrumbs: (breadcrumbs: any[]) => void;

  editorVisible: boolean;
  setEditorVisible: (editorVisible: boolean) => void;

  selectedMetadataTagZone: MetadataTagZone;
  setSelectedMetadataTagZone: (
    selectedMetadataTagZone: MetadataTagZone
  ) => void;

  newMetadataTagZone: MetadataTagZone;
  setNewMetadataTagZone: (newMetadataTagZone: MetadataTagZone) => void;

  editsMade: boolean;
  setEditsMade: (editsMade: boolean) => void;

  pageLoading: boolean;
  setPageLoading: (pageLoading: boolean) => void;

  largerMode: boolean;
  setLargerMode: (largerMode: boolean) => void;

  adjustedDimensions: {
    x: number;
    y: number;
  };
  setAdjustedDimensions: (adjustedDimensions: { x: number; y: number }) => void;

  dimensions: {
    width: number;
    height: number;
  };

  setDimensions: (dimensions: { width: number; height: number }) => void;

  shapesArray: number[][][];
  setShapesArray: (shapesArray: number[][][]) => void;

  selectedShapeIndex: number | null;
  setSelectedShapeIndex: (selectedShapeIndex: number | null) => void;

  selectedMetadataTagZoneIndex: number;
  setSelectedMetadataTagZoneIndex: (
    selectedMetadataTagZoneIndex: number
  ) => void;

  editedTagZoneIndexes: number[];
  setEditedTagZoneIndexes: (editedTagZoneIndexes: number[]) => void;

  mtzPolygonPoints: number[][];
  setMtzPolygonPoints: (mtzPolygonPoints: number[][]) => void;

  showAllMetadataTagZones: boolean;
  setShowAllMetadataTagZones: (showAllMetadataTagZones: boolean) => void;

  shapeColor: string;
  setShapeColor: (shapeColor: string) => void;

  shapeColorIndex: number;
  setShapeColorIndex: (shapeColorIndex: number) => void;

  imageURLmdtz: string;
  setImageURLmdtz: (imageURLmdtz: string) => void;

  metricLabels: MetricLabel[];
  setMetricLabels: (metricLabels: MetricLabel[]) => void;

  addMetaDataIgnoreZoneDialogOpen: boolean;
  setAddMetaDataIgnoreZoneDialogOpen: (
    addMetaDataIgnoreZoneDialogOpen: boolean
  ) => void;

  instructionsDialogOpen: boolean;
  setInstructionsDialogOpen: (instructionsDialogOpen: boolean) => void;

  mdtzBuilder: JSX.Element;
  setMdtzBuilder: (mdtzBuilder: JSX.Element) => void;

  getAllEditMetaDataTagZonesPageInfo: (
    navigate: NavigateFunction
  ) => Promise<void>;

  handlePostMetadataTagZones: (navigate: NavigateFunction) => Promise<void>;

  handleDeleteMetadataTagZone: (
    tagZoneId: number,
    navigate: NavigateFunction
  ) => Promise<void>;
}

export const useEditIMetadataTagZonesStore = create<EditIMetadataTagZonesStore>(
  (set, get) => ({
    metadataTagZones: [],
    setMetadataTagZones: (metadataTagZones: MetadataTagZones) =>
      set({ metadataTagZones }),

    cameraInfo: {
      camera_name: "",
    },
    setCameraInfo: (cameraInfo: CameraInfo) => set({ cameraInfo }),

    breadcrumbs: [],
    setBreadcrumbs: (breadcrumbs: any[]) => set({ breadcrumbs }),

    editorVisible: false,
    setEditorVisible: (editorVisible: boolean) => set({ editorVisible }),

    selectedMetadataTagZone: {
      // Don't include id property - let API assign it
      camera: Number(getNumbersAfterEditCamera(location.href)),
      name: "",
      description: "",
      line_color: "",
      coordinates: [],
      metric_labels: [], // Array of metric label IDs
    },

    newMetadataTagZone: {
      // Don't include id property - let API assign it
      camera: Number(getNumbersAfterEditCamera(location.href)),
      name: "",
      description: "",
      line_color: "",
      coordinates: [],
      metric_labels: [], // Array of metric label IDs
    },

    setNewMetadataTagZone: (newMetadataTagZone: MetadataTagZone) =>
      set({ newMetadataTagZone }),

    setSelectedMetadataTagZone: (selectedMetadataTagZone: MetadataTagZone) =>
      set({ selectedMetadataTagZone }),

    editsMade: false,
    setEditsMade: (editsMade: boolean) => set({ editsMade }),

    pageLoading: true,
    setPageLoading: (pageLoading: boolean) => set({ pageLoading }),

    largerMode: false,
    setLargerMode: (largerMode: boolean) => set({ largerMode }),

    adjustedDimensions: {
      x: 720,
      y: 576,
    },
    setAdjustedDimensions: (adjustedDimensions: { x: number; y: number }) =>
      set({ adjustedDimensions }),

    dimensions: {
      width: 720,
      height: 576,
    },
    setDimensions: (dimensions: { width: number; height: number }) =>
      set({ dimensions }),

    shapesArray: [],
    setShapesArray: (shapesArray: number[][][]) => set({ shapesArray }),

    selectedShapeIndex: 0,
    setSelectedShapeIndex: (selectedShapeIndex: number | null) =>
      set({ selectedShapeIndex }),

    selectedMetadataTagZoneIndex: 0,
    setSelectedMetadataTagZoneIndex: (selectedMetadataTagZoneIndex: number) =>
      set({ selectedMetadataTagZoneIndex }),

    editedTagZoneIndexes: [],
    setEditedTagZoneIndexes: (editedTagZoneIndexes: number[]) =>
      set({ editedTagZoneIndexes }),

    mtzPolygonPoints: [],
    setMtzPolygonPoints: (mtzPolygonPoints: number[][]) =>
      set({ mtzPolygonPoints }),

    showAllMetadataTagZones: false,
    setShowAllMetadataTagZones: (showAllMetadataTagZones: boolean) =>
      set({ showAllMetadataTagZones }),

    shapeColor: "",
    setShapeColor: (shapeColor: string) => set({ shapeColor }),

    shapeColorIndex: 0,
    setShapeColorIndex: (shapeColorIndex: number) => set({ shapeColorIndex }),

    imageURLmdtz: "",
    setImageURLmdtz: (imageURLmdtz: string) => set({ imageURLmdtz }),

    metricLabels: [],
    setMetricLabels: (metricLabels: MetricLabel[]) => set({ metricLabels }),

    addMetaDataIgnoreZoneDialogOpen: false,
    setAddMetaDataIgnoreZoneDialogOpen: (
      addMetaDataIgnoreZoneDialogOpen: boolean
    ) => set({ addMetaDataIgnoreZoneDialogOpen }),

    instructionsDialogOpen: false,
    setInstructionsDialogOpen: (instructionsDialogOpen: boolean) =>
      set({ instructionsDialogOpen }),

    mdtzBuilder: React.createElement(React.Fragment),
    setMdtzBuilder: (mdtzBuilder: JSX.Element) => set({ mdtzBuilder }),

    getAllEditMetaDataTagZonesPageInfo: async (navigate: NavigateFunction) => {
      const {
        getAllEditMetaDataTagZonesPageInfo,
        setDimensions,
        setAdjustedDimensions,
        setImageURLmdtz,
        setPageLoading,
        setMetadataTagZones,
        setBreadcrumbs,
        setMetricLabels,
        setCameraInfo,
      } = get();
      const AxiosUI = createApi(``);
      const cameraId: any = getNumbersAfterEditCamera(location.href);
      const retryFunction = () => {
        getAllEditMetaDataTagZonesPageInfo(navigate);
      };

      try {
        // Run all API calls concurrently using Promise.all
        const [
          cameraStatusResponse,
          tagZonesResponse,
          customerAboutResponse,
          metricLabelsResponse,
        ] = await Promise.all([
          AxiosUI.get(`/camera/${cameraId}/status/`),
          AxiosUI.get(`tagzone/?camera=${cameraId}`),
          AxiosUI.get(
            `/customer/${getNumbersAfterSites(location.href)}/about/`
          ),
          AxiosUI.get(`/metric_label/?camera=${cameraId}`),
        ]);

        // Process all responses
        setDimensions(
          resizeImageMetadataTagZones(
            cameraStatusResponse.data.frame_dimensions
          )
        );
        const adjustedDims = adjustDimensions(
          cameraStatusResponse.data.frame_dimensions
        );
        if (adjustedDims) {
          setAdjustedDimensions(adjustedDims);
        }
        setCameraInfo(cameraStatusResponse.data);
        setImageURLmdtz(cameraStatusResponse.data.preview_url);
        setMetadataTagZones(tagZonesResponse.data.results);
        setBreadcrumbs(customerAboutResponse.data.breadcrumbs);
        setMetricLabels(metricLabelsResponse.data);

        // Set loading to false only after all requests complete successfully
        setPageLoading(false);
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            alert(
              "Unable to load page information. Please try refreshing the page."
            );
            setPageLoading(false);
          }
        );
      }
    },
    handlePostMetadataTagZones: async (navigate: NavigateFunction) => {
      const {
        handlePostMetadataTagZones,
        metadataTagZones,
        setPageLoading,
        getAllEditMetaDataTagZonesPageInfo,
      } = get();
      const AxiosUI = createApi(``);
      const retryFunction = () => {
        handlePostMetadataTagZones(navigate);
      };

      try {
        // Create array of promises for all API calls
        const apiPromises = metadataTagZones.map(
          (metadataTagZone: MetadataTagZone) => {
            if (!metadataTagZone.id) {
              return AxiosUI.post(`/tagzone/`, metadataTagZone);
            } else {
              return AxiosUI.patch(
                `/tagzone/${metadataTagZone.id}/`,
                metadataTagZone
              );
            }
          }
        );

        // Wait for all API calls to complete
        await Promise.all(apiPromises);

        // Refresh the data to get updated zones with proper IDs
        await getAllEditMetaDataTagZonesPageInfo(navigate);

        navigate(
          `/sites/${getNumbersAfterSites(
            location.href
          )}/editcamera/${getNumbersAfterEditCamera(location.href)}/`
        );

        // Set loading to false only after all requests complete successfully
        setPageLoading(false);
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            alert("Unable to post metadata tag zones. Please try again.");
            setPageLoading(false);
          }
        );
      }
    },
    handleDeleteMetadataTagZone: async (
      tagZoneId: number,
      navigate: NavigateFunction
    ) => {
      const {
        handleDeleteMetadataTagZone,
        setPageLoading,
        setMetadataTagZones,
      } = get();
      const AxiosUI = createApi(``);
      const cameraId: any = getNumbersAfterEditCamera(location.href);
      const retryFunction = () => {
        handleDeleteMetadataTagZone(tagZoneId, navigate);
      };

      try {
        // Delete the metadata tag zone
        await AxiosUI.delete(`/tagzone/${tagZoneId}/`);

        // Fetch updated tag zones
        const tagZonesResponse = await AxiosUI.get(
          `tagzone/?camera=${cameraId}`
        );
        setMetadataTagZones(tagZonesResponse.data.results);

        // Set loading to false only after all requests complete successfully
        setPageLoading(false);
      } catch (reason) {
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          () => {
            alert("Unable to delete metadata tag zone. Please try again.");
            setPageLoading(false);
          }
        );
      }
    },
  })
);
